import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import type { ConversationRecord, ConversationsAdapter } from '../sync/persistence';
import { logger } from '../log/log';

let persistenceAdapter: ConversationsAdapter | null = null;

async function getPersistenceAdapter(): Promise<ConversationsAdapter> {
  if (persistenceAdapter !== null) {
    return persistenceAdapter;
  }

  if (Platform.OS === 'web') {
    const { createWebConversationsAdapter } = await import('../sync/conversationsAdapter.web');
    persistenceAdapter = createWebConversationsAdapter();
  } else {
    const { createMacosConversationsAdapter } = await import('../sync/conversationsAdapter.macos');
    persistenceAdapter = createMacosConversationsAdapter();
  }

  return persistenceAdapter;
}

export interface UseConversationsResult {
  conversations: ConversationRecord[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useConversations(): UseConversationsResult {
  const [conversations, setConversations] = useState<ConversationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [adapter, setAdapter] = useState<ConversationsAdapter | null>(null);

  useEffect(() => {
    let mounted = true;

    getPersistenceAdapter()
      .then((persistenceAdapterInstance) => {
        if (!mounted) return;
        setAdapter(persistenceAdapterInstance);
      })
      .catch((err) => {
        if (!mounted) return;
        logger.error('useConversations failed to get persistence adapter', err as Error);
        setError(err as Error);
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (adapter === null) return;

    const unsubscribe = adapter.watchConversations(
      (records) => {
        setConversations(records);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        // The watch itself failed (e.g. a Dexie read error). Surface it and
        // clear the spinner so the list shows an error state with a retry,
        // instead of an infinite skeleton.
        logger.error('useConversations watch failed', err);
        setError(err);
        setIsLoading(false);
      },
    );

    return unsubscribe;
  }, [adapter]);

  const refresh = async () => {
    if (adapter === null) {
      logger.debug('useConversations.refresh called before adapter ready');
      return;
    }

    try {
      await adapter.refreshConversations();
    } catch (err) {
      logger.error('useConversations.refresh failed', err as Error);
      setError(err as Error);
    }
  };

  return {
    conversations,
    isLoading,
    error,
    refresh,
  };
}
